import { useEffect, useState } from 'react';

import { HatIcon, User02Icon, UserGroupIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Permissions } from 'constants/permissions';
import { useDocumentTitle } from 'hooks/document-title';
import AppPage from 'layouts/AppPage/AppPage';
import { Tab } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { OverflowTabs } from 'components/Tabs/Tabs';

import { messagePopUp } from 'api/app-slice';
import { useLazyGetCompanyUsersQuery } from 'api/company-api';
import {
  createSSOOrganization,
  disableSSOOrganization,
  selectCompany,
  updateCompanyOwner,
} from 'api/company-slice';
import { createRole, deleteRole, getRoles, selectRoles, updateRole } from 'api/roles-slice';
import { createTeam, deleteTeam, getTeams, selectTeams, updateTeam } from 'api/teams-slice';
import { useDeleteUserMutation } from 'api/user-api';

import { RolesTab } from './tabs/RolesTab/RolesTab';
import { TeamsTab } from './tabs/TeamsTab/TeamsTab';
import { UsersTab } from './tabs/UsersTab/UsersTab';

const Users = () => {
  useDocumentTitle('Users');

  const dispatch = useDispatch();

  const company = useSelector(selectCompany);
  const teams = useSelector(selectTeams);
  const roles = useSelector(selectRoles);

  const [getCompanyUsers] = useLazyGetCompanyUsersQuery();
  const [deleteUser] = useDeleteUserMutation();

  const [loading, setLoading] = useState(true);

  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!company.id) return;

    Promise.all([
      getCompanyUsers({ id: company.id, params: { records: 1000 } }).unwrap(),
      dispatch(getRoles()),
      dispatch(getTeams()),
    ]).then(response => {
      setUsers(response[0].result.users);
      setLoading(false);
    });
  }, [company]);

  return (
    <AppPage loading={loading} requiredPermissions={[Permissions.CompanyWrite]}>
      <div>
        <AppPage.Header
          title="User Access"
          subtitle="Below you can view a list of your current users."
        />
        <OverflowTabs defaultActiveKey="users" className="mt-12 mx-12" mountOnEnter={true}>
          <Tab
            eventKey="users"
            title={
              <span className="flex items-center gap-2">
                <HugeiconsIcon icon={User02Icon} size="16" strokeWidth={2} /> Users
              </span>
            }>
            {company.id && (
              <UsersTab
                company={company}
                roles={roles}
                teams={teams}
                getCompanyUsers={getCompanyUsers}
                messagePopUp={params => dispatch(messagePopUp(params))}
                setLoading={setLoading}
                deleteUser={deleteUser}
                updateCompanyOwner={params => dispatch(updateCompanyOwner(params))}
                createSSOOrganization={params => dispatch(createSSOOrganization(params))}
                disableSSOOrganization={() => dispatch(disableSSOOrganization())}
              />
            )}
          </Tab>
          <Tab
            eventKey="roles"
            title={
              <span className="flex items-center gap-2">
                <HugeiconsIcon icon={HatIcon} size="16" strokeWidth={2} /> Roles
              </span>
            }>
            <RolesTab
              users={users}
              roles={roles}
              updateRole={(id, data) => dispatch(updateRole({ id, data }))}
              deleteRole={id => dispatch(deleteRole(id))}
              createRole={data => dispatch(createRole(data))}
              setLoading={setLoading}
            />
          </Tab>
          <Tab
            eventKey="teams"
            title={
              <span className="flex items-center gap-2">
                <HugeiconsIcon icon={UserGroupIcon} size="16" strokeWidth={2} /> Teams
              </span>
            }>
            <TeamsTab
              users={users}
              teams={teams}
              createTeam={id => dispatch(createTeam(id))}
              updateTeam={(id, data) => dispatch(updateTeam({ id, data }))}
              deleteTeam={id => dispatch(deleteTeam(id))}
              setLoading={setLoading}
            />
          </Tab>
        </OverflowTabs>
      </div>
    </AppPage>
  );
};

export default Users;
